import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useProfile } from '../../contexts/ProfileContext';
import { isAdminFromUser } from '../../utils/auth/adminClaims';
import {
  deleteManualDailyPuzzleAdmin,
  ensureDailyBufferAdmin,
  listDailyPuzzlesAdmin,
  regenerateDailyImageAdmin,
  regenerateDailyRiddleAdmin,
  setManualDailyPuzzleAdmin,
  updateDailyZoomOriginAdmin,
  uploadDailyCustomImageAdmin,
} from '../../utils/dailyAdminApi';
import {
  DAILY_INTERPOLATION_INITIAL_ZOOM,
  DAILY_ZOOM_BOUNDS,
} from '../../constants/games/dailyGame';
import DailyWordCell from './DailyWordCell';
import DailyImageCell from './DailyImageCell';
import DailyZoomOriginEditorModal from './DailyZoomOriginEditorModal';
import ImagePreviewModal from '../games/shared/ImagePreviewModal';
import './DailyPuzzleAdminPage.css';

const todayUtcDate = () => new Date().toISOString().slice(0, 10);
const toSafeZoomOrigin = (row) => {
  const x = Number(row?.zoom_origin_x);
  const y = Number(row?.zoom_origin_y);
  return {
    x: Number.isFinite(x) ? x : 0.5,
    y: Number.isFinite(y) ? y : 0.5,
  };
};

const toVisionContextObject = (value) => {
  if (!value) return null;
  if (typeof value === 'object' && !Array.isArray(value)) return value;
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed;
      }
    } catch {
      return null;
    }
  }
  return null;
};

const toVisionContextPreview = (row) => {
  const context = toVisionContextObject(row?.riddle_image_context);
  if (!context) return null;

  const summary = typeof context.summary === 'string' ? context.summary.trim() : '';
  const keyVisuals = Array.isArray(context.key_visuals)
    ? context.key_visuals
        .map((item) => String(item || '').trim())
        .filter(Boolean)
        .slice(0, 4)
    : [];
  const confidenceRaw = Number(context.confidence);
  const confidence = Number.isFinite(confidenceRaw)
    ? `${Math.round(Math.max(0, Math.min(1, confidenceRaw)) * 100)}%`
    : null;

  return {
    summary,
    keyVisuals,
    confidence,
  };
};

const DailyPuzzleAdminPage = () => {
  const { user } = useProfile();
  const isAdmin = useMemo(() => isAdminFromUser(user), [user]);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [revealedWordDates, setRevealedWordDates] = useState({});
  const [wordRevealArmedDate, setWordRevealArmedDate] = useState('');
  const [revealedImageDates, setRevealedImageDates] = useState({});
  const [imageRevealArmedDate, setImageRevealArmedDate] = useState('');

  const [formDate, setFormDate] = useState(todayUtcDate());
  const [formWord, setFormWord] = useState('');
  const [formImageUrl, setFormImageUrl] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [startZoomPreviewRow, setStartZoomPreviewRow] = useState(null);
  const [zoomOriginEditorRow, setZoomOriginEditorRow] = useState(null);

  const clearNotice = () => {
    setErrorMessage('');
    setSuccessMessage('');
  };

  const loadRows = useCallback(async () => {
    if (!isAdmin) return;
    setLoading(true);
    clearNotice();
    try {
      const payload = await listDailyPuzzlesAdmin();
      setRows(Array.isArray(payload?.rows) ? payload.rows : []);
      setRevealedWordDates({});
      setWordRevealArmedDate('');
      setRevealedImageDates({});
      setImageRevealArmedDate('');
    } catch (error) {
      setErrorMessage(error?.message || 'Failed to load daily admin rows.');
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const onUploadImage = async () => {
    if (!selectedFile) {
      setErrorMessage('Choose an image file first.');
      return;
    }
    clearNotice();
    setBusy(true);
    try {
      const payload = await uploadDailyCustomImageAdmin({
        file: selectedFile,
        puzzleDate: formDate,
      });
      setFormImageUrl(payload?.imageUrl || '');
      setSuccessMessage('Image uploaded to R2.');
    } catch (error) {
      setErrorMessage(error?.message || 'Image upload failed.');
    } finally {
      setBusy(false);
    }
  };

  const onSaveManualPuzzle = async () => {
    const normalizedWord = formWord.trim().toLowerCase();
    if (!/^[a-z]{5}$/.test(normalizedWord)) {
      setErrorMessage('Word must be exactly 5 lowercase letters.');
      return;
    }
    if (!formImageUrl.trim()) {
      setErrorMessage('Upload an image or paste a valid image URL.');
      return;
    }

    clearNotice();
    setBusy(true);
    try {
      await setManualDailyPuzzleAdmin({
        puzzleDate: formDate,
        word: normalizedWord,
        imageUrl: formImageUrl.trim(),
      });
      setSuccessMessage('Manual daily puzzle saved.');
      await loadRows();
    } catch (error) {
      setErrorMessage(error?.message || 'Saving manual puzzle failed.');
    } finally {
      setBusy(false);
    }
  };

  const onEnsureBuffer = async () => {
    clearNotice();
    setBusy(true);
    try {
      const payload = await ensureDailyBufferAdmin();
      const generated = Number(payload?.generatedCount || 0);
      setSuccessMessage(`Ensure buffer completed. Generated ${generated} puzzle(s).`);
      await loadRows();
    } catch (error) {
      setErrorMessage(error?.message || 'Ensure buffer failed.');
    } finally {
      setBusy(false);
    }
  };

  const onDeleteManual = async (date) => {
    clearNotice();
    setBusy(true);
    try {
      await deleteManualDailyPuzzleAdmin(date);
      setSuccessMessage(`Deleted manual puzzle for ${date}.`);
      await loadRows();
    } catch (error) {
      setErrorMessage(error?.message || 'Failed to delete manual puzzle.');
    } finally {
      setBusy(false);
    }
  };

  const onRegenerateRiddle = async (row) => {
    const targetDate = typeof row?.puzzle_date === 'string' ? row.puzzle_date : '';
    if (!targetDate) return;

    clearNotice();
    setBusy(true);
    try {
      await regenerateDailyRiddleAdmin(targetDate);
      setSuccessMessage(`Regenerated riddle for ${targetDate}.`);
      await loadRows();
    } catch (error) {
      setErrorMessage(error?.message || `Failed to regenerate riddle for ${targetDate}.`);
    } finally {
      setBusy(false);
    }
  };

  const onRegenerateImage = async (row) => {
    const targetDate = typeof row?.puzzle_date === 'string' ? row.puzzle_date : '';
    if (!targetDate) return;

    const didConfirm = window.confirm(
      `Regenerate photo for ${targetDate}? This also regenerates riddle/context.`,
    );
    if (!didConfirm) return;

    clearNotice();
    setBusy(true);
    try {
      const payload = await regenerateDailyImageAdmin(targetDate);
      const warning = typeof payload?.riddleWarning === 'string' ? payload.riddleWarning.trim() : '';
      setSuccessMessage(
        warning
          ? `Regenerated photo for ${targetDate}. ${warning}`
          : `Regenerated photo and riddle for ${targetDate}.`,
      );
      await loadRows();
    } catch (error) {
      setErrorMessage(error?.message || `Failed to regenerate photo for ${targetDate}.`);
    } finally {
      setBusy(false);
    }
  };

  const copyToClipboard = async (value) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setSuccessMessage('Copied image URL.');
    } catch {
      setErrorMessage('Could not copy image URL.');
    }
  };

  const onToggleWord = (row) => {
    const dateKey = String(row?.puzzle_date || row?.id || '');
    const hasWord = typeof row?.word === 'string' && row.word.trim().length > 0;
    if (!dateKey || !hasWord) return;

    const isVisible = Boolean(revealedWordDates[dateKey]);
    if (isVisible) {
      setRevealedWordDates((previous) => {
        const next = { ...previous };
        delete next[dateKey];
        return next;
      });
      if (wordRevealArmedDate === dateKey) {
        setWordRevealArmedDate('');
      }
      return;
    }

    if (wordRevealArmedDate === dateKey) {
      setRevealedWordDates((previous) => ({ ...previous, [dateKey]: true }));
      setWordRevealArmedDate('');
      return;
    }

    setWordRevealArmedDate(dateKey);
  };

  const onToggleImage = (row) => {
    const dateKey = String(row?.puzzle_date || row?.id || '');
    const hasImage = typeof row?.image_url === 'string' && row.image_url.trim().length > 0;
    if (!dateKey || !hasImage) return;

    const isVisible = Boolean(revealedImageDates[dateKey]);
    if (isVisible) {
      setRevealedImageDates((previous) => {
        const next = { ...previous };
        delete next[dateKey];
        return next;
      });
      if (imageRevealArmedDate === dateKey) {
        setImageRevealArmedDate('');
      }
      return;
    }

    if (imageRevealArmedDate === dateKey) {
      setRevealedImageDates((previous) => ({ ...previous, [dateKey]: true }));
      setImageRevealArmedDate('');
      return;
    }

    setImageRevealArmedDate(dateKey);
  };

  const openStartZoomPreview = (row) => {
    if (!row?.image_url) return;
    setStartZoomPreviewRow(row);
  };

  const closeStartZoomPreview = () => {
    setStartZoomPreviewRow(null);
  };

  const openZoomOriginEditor = (row) => {
    if (!row?.image_url) return;
    setZoomOriginEditorRow(row);
  };

  const closeZoomOriginEditor = () => {
    if (busy) return;
    setZoomOriginEditorRow(null);
  };

  const onSaveZoomOrigin = async ({ puzzleDate, zoomOriginX, zoomOriginY }) => {
    if (!puzzleDate) return;
    clearNotice();
    setBusy(true);
    try {
      await updateDailyZoomOriginAdmin({
        puzzleDate,
        zoomOriginX,
        zoomOriginY,
      });
      setSuccessMessage(`Updated start zoom for ${puzzleDate}.`);
      setZoomOriginEditorRow(null);
      await loadRows();
    } catch (error) {
      setErrorMessage(error?.message || `Failed to update start zoom for ${puzzleDate}.`);
    } finally {
      setBusy(false);
    }
  };

  if (!user) {
    return (
      <div className="daily-admin-page">
        <p>Sign in with an admin account to access this page.</p>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="daily-admin-page">
        <p>Admin access required.</p>
      </div>
    );
  }

  return (
    <div className="daily-admin-page">
      <div className="daily-admin-toolbar">
        <button onClick={loadRows} disabled={loading || busy}>
          Refresh List
        </button>
        <button onClick={onEnsureBuffer} disabled={loading || busy}>
          Ensure 7-Day Buffer
        </button>
      </div>

      <section className="daily-admin-card">
        <h3>Set Manual Puzzle</h3>
        <div className="daily-admin-form">
          <label>
            Date (UTC)
            <input
              type="date"
              value={formDate}
              onChange={(event) => setFormDate(event.target.value)}
              disabled={busy}
            />
          </label>
          <label>
            Word (5 letters)
            <input
              type="text"
              value={formWord}
              onChange={(event) => setFormWord(event.target.value)}
              maxLength={5}
              placeholder="e.g. apple"
              disabled={busy}
            />
          </label>
          <label>
            Upload image
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp"
              onChange={(event) => setSelectedFile(event.target.files?.[0] || null)}
              disabled={busy}
            />
          </label>
          <button onClick={onUploadImage} disabled={busy || !selectedFile}>
            Upload to R2
          </button>
          <label>
            Image URL
            <input
              type="text"
              value={formImageUrl}
              onChange={(event) => setFormImageUrl(event.target.value)}
              placeholder="https://cdn.yourdomain.com/daily/YYYY-MM-DD/file.jpg"
              disabled={busy}
            />
          </label>
          <button onClick={onSaveManualPuzzle} disabled={busy}>
            Save Manual Puzzle
          </button>
        </div>
      </section>

      {errorMessage && <div className="daily-admin-notice daily-admin-notice--error">{errorMessage}</div>}
      {successMessage && (
        <div className="daily-admin-notice daily-admin-notice--success">{successMessage}</div>
      )}
      {startZoomPreviewRow?.image_url ? (
        <ImagePreviewModal
          imageUrl={startZoomPreviewRow.image_url}
          alt={
            startZoomPreviewRow?.puzzle_date
              ? `Daily start zoom preview for ${startZoomPreviewRow.puzzle_date}`
              : 'Daily start zoom preview'
          }
          zoomLevel={DAILY_INTERPOLATION_INITIAL_ZOOM}
          zoomOrigin={toSafeZoomOrigin(startZoomPreviewRow)}
          zoomBounds={DAILY_ZOOM_BOUNDS}
          onClose={closeStartZoomPreview}
        />
      ) : null}
      {zoomOriginEditorRow ? (
        <DailyZoomOriginEditorModal
          row={zoomOriginEditorRow}
          busy={busy}
          onClose={closeZoomOriginEditor}
          onSave={onSaveZoomOrigin}
        />
      ) : null}

      <section className="daily-admin-card">
        <h3>Upcoming 14 Days</h3>
        <p className="daily-admin-word-hint">
          Words and images are hidden by default. Click once to arm reveal, click again to reveal,
          click again to hide.
        </p>
        {loading ? (
          <p>Loading...</p>
        ) : (
          <div className="daily-admin-table-wrap">
            <table className="daily-admin-table">
              <thead>
                <tr>
                  <th>Date (UTC)</th>
                  <th>Source</th>
                  <th>Word</th>
                  <th>Image</th>
                  <th>Image URL</th>
                  <th>Riddle Preview</th>
                  <th>Vision Context</th>
                  <th>Regenerate Riddle</th>
                  <th>Regenerate Photo</th>
                  <th>Adjust Start Zoom</th>
                  <th>Status</th>
                  <th>Start Zoom</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => {
                  if (!row) {
                    return (
                      <tr key={`empty-${index}`}>
                        <td colSpan={13}>No puzzle scheduled for this date yet.</td>
                      </tr>
                    );
                  }
                  const dateKey = String(row.puzzle_date || row.id || index);
                  const riddlePreview =
                    typeof row?.riddle_text === 'string' ? row.riddle_text.trim() : '';
                  const visionContext = toVisionContextPreview(row);
                  return (
                    <tr key={row.id || `${row.puzzle_date}-${index}`}>
                      <td>{row.puzzle_date}</td>
                      <td>{row.source}</td>
                      <td>
                        <DailyWordCell
                          word={row.word}
                          isVisible={Boolean(revealedWordDates[dateKey])}
                          isArmed={wordRevealArmedDate === dateKey}
                          onToggle={() => onToggleWord(row)}
                        />
                      </td>
                      <td>
                        <DailyImageCell
                          imageUrl={row.image_url}
                          isVisible={Boolean(revealedImageDates[dateKey])}
                          isArmed={imageRevealArmedDate === dateKey}
                          onToggle={() => onToggleImage(row)}
                        />
                      </td>
                      <td>
                        <div className="daily-admin-url-cell">
                          <span title={row.image_url}>{row.image_url || '-'}</span>
                          {row.image_url ? (
                            <button onClick={() => copyToClipboard(row.image_url)}>Copy</button>
                          ) : null}
                        </div>
                      </td>
                      <td>
                        {riddlePreview ? (
                          <span className="daily-admin-riddle-preview" title={riddlePreview}>
                            {riddlePreview}
                          </span>
                        ) : (
                          <span className="daily-admin-riddle-preview daily-admin-riddle-preview--empty">
                            Not generated yet
                          </span>
                        )}
                      </td>
                      <td>
                        {visionContext ? (
                          <div className="daily-admin-vision-context">
                            <div
                              className="daily-admin-vision-context__summary"
                              title={visionContext.summary}
                            >
                              {visionContext.summary || 'No summary'}
                            </div>
                            {visionContext.keyVisuals.length ? (
                              <div className="daily-admin-vision-context__meta">
                                Visuals: {visionContext.keyVisuals.join(', ')}
                              </div>
                            ) : null}
                            <div className="daily-admin-vision-context__meta">
                              Model: {row.riddle_image_context_model || '-'}
                              {visionContext.confidence
                                ? ` • Confidence: ${visionContext.confidence}`
                                : ''}
                            </div>
                          </div>
                        ) : (
                          <span className="daily-admin-riddle-preview daily-admin-riddle-preview--empty">
                            No vision context yet
                          </span>
                        )}
                      </td>
                      <td>
                        <button onClick={() => onRegenerateRiddle(row)} disabled={busy}>
                          Regenerate
                        </button>
                      </td>
                      <td>
                        <button onClick={() => onRegenerateImage(row)} disabled={busy}>
                          Regenerate
                        </button>
                      </td>
                      <td>
                        {row.image_url ? (
                          <button onClick={() => openZoomOriginEditor(row)} disabled={busy}>
                            Adjust
                          </button>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td>{row.status}</td>
                      <td>
                        {row.image_url ? (
                          <button onClick={() => openStartZoomPreview(row)}>Open</button>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td>
                        {row.source === 'manual' ? (
                          <button onClick={() => onDeleteManual(row.puzzle_date)} disabled={busy}>
                            Delete Manual
                          </button>
                        ) : (
                          '-'
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
};

export default DailyPuzzleAdminPage;

